/**
 * @fileoverview Host-injected local-first lookup reader for shared ui-admin surfaces.
 * @module ui-admin/contexts/LookupDataSourceContext
 *
 * Responsibilities:
 * - Provide one Dexie-backed lookup reader to shared components and hooks.
 * - Keep host persistence dependencies outside the ui-admin package.
 *
 * Dependencies: React and the shared LookupValue contract.
 * Architecture: Host-owned reader is injected through a React context boundary.
 * Notes: Consumers may still provide an explicit reader for focused composition.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { LookupValue } from '../services/lookupService';

/**
 * @contract LookupValuesReader
 * Purpose: Describes the host-owned local-first query for a lookup category.
 * Fields: lookupType argument and Promise of matching lookup values.
 * Rules: The reader returns the host's current Dexie-backed records.
 * Sync: Reads local state; sync reconciliation is owned by the host.
 */
export type LookupValuesReader = (lookupType: string) => Promise<LookupValue[]>;

/**
 * @contract LookupDataSource
 * Purpose: Defines the lookup query dependency shared ui-admin hooks require.
 * Fields: listLookupValues host reader.
 * Rules: No host database implementation is imported into ui-admin.
 * Sync: Delegates to the host's local-first service.
 */
export interface LookupDataSource {
  listLookupValues: LookupValuesReader;
}

const LookupDataSourceContext = createContext<LookupDataSource | undefined>(undefined);

/**
 * @contract LookupDataSourceProviderProps
 * Purpose: Defines the provider's injected lookup reader and rendered subtree.
 * Fields: listLookupValues and React children.
 * Rules: Host code owns the reader; the provider does not create fallback data.
 * Sync: The reader continues to serve host-local state and sync reconciliation.
 */
export interface LookupDataSourceProviderProps extends LookupDataSource {
  children: ReactNode;
}

/**
 * @component LookupDataSourceProvider
 * Purpose: Supply the host's Dexie-backed lookup reader to shared ui-admin controls.
 * Props: listLookupValues (host service reader), children (shared UI subtree).
 * Behavior: Makes the reader available to hooks beneath the provider.
 * Data Source: Local (host service backed by Dexie).
 * Constraints: Does not import a host database or perform lookup reads itself.
 * Performance: Passes one reader reference to the subtree.
 * Side Effects: none; this provider only exposes the injected reader.
 * Offline Behavior: Uses the reader's host-local Dexie data.
 */
export function LookupDataSourceProvider({
  listLookupValues,
  children,
}: LookupDataSourceProviderProps) {
  return (
    <LookupDataSourceContext.Provider value={{ listLookupValues }}>
      {children}
    </LookupDataSourceContext.Provider>
  );
}

/**
 * @hook useLookupDataSource
 * Purpose: Read the current host's lookup data-source contract.
 * Returns: The injected `{ listLookupValues }` value, or undefined outside a provider.
 * Side Effects: none.
 * Offline Behavior: A mounted provider delegates reads to its host's local-first service.
 */
export function useLookupDataSource(): LookupDataSource | undefined {
  return useContext(LookupDataSourceContext);
}
